import { useMemo } from 'react';
import type { SessionState, IntakePortalSchema, ProgressStatus, SessionStateCommunity, SessionStatePerson, TrackedField } from '@/models/types';
import { getFieldType, isPersonField } from '@/config/fieldTypes';

interface ProgressCalculationResult {
  welcome: ProgressStatus;
  search: ProgressStatus;
  confirm: ProgressStatus;
  submit: ProgressStatus;
  char_income: ProgressStatus;
  char_section8: ProgressStatus;
  char_newconstruction: ProgressStatus;
  char_student: ProgressStatus;
  char_sharedops: ProgressStatus;
  char_submit: ProgressStatus;
  impl_project_lead: ProgressStatus;
  impl_technical: ProgressStatus;
  impl_voice: ProgressStatus;
  impl_marketing: ProgressStatus;
  impl_knowledge: ProgressStatus;
  impl_training: ProgressStatus;
  impl_owner: ProgressStatus;
  impl_submit: ProgressStatus;
  // PLG
  plg_name: ProgressStatus;
  plg_ai_name: ProgressStatus;
  plg_voice_area: ProgressStatus;
  plg_schedule: ProgressStatus;
  plg_submit: ProgressStatus;
}

export const useProgressCalculation = (
  sessionState: SessionState | undefined,
  schema: IntakePortalSchema | undefined
): ProgressCalculationResult => {
  return useMemo(() => {
    if (!sessionState || !schema) {
      return {
        welcome: 'pending',
        search: 'pending', 
        confirm: 'pending',
        submit: 'pending',
        char_income: 'pending',
        char_section8: 'pending',
        char_newconstruction: 'pending',
        char_student: 'pending',
        char_sharedops: 'pending',
        char_submit: 'pending',
        impl_project_lead: 'pending',
        impl_technical: 'pending',
        impl_voice: 'pending',
        impl_marketing: 'pending',
        impl_knowledge: 'pending',
        impl_training: 'pending',
        impl_owner: 'pending',
        impl_submit: 'pending',
        plg_name: 'pending',
        plg_ai_name: 'pending',
        plg_voice_area: 'pending',
        plg_schedule: 'pending',
        plg_submit: 'pending',
      };
    }
    
    // Welcome is always complete (static page)
    const welcome: ProgressStatus = 'completed';
    
    // Search: complete if at least one community
    const search: ProgressStatus = sessionState.communities.length > 0 ? 'completed' : 'in_progress';
    
    // Confirm: complete if all required fields are valid
    const confirm: ProgressStatus = search === 'completed' ? 
      calculateConfirmProgress(sessionState.communities, schema) : 'pending';
    
    // Submit: complete if submitted flag is true
    const submit: ProgressStatus = confirm === 'completed' ?
      (sessionState.submitted ? 'completed' : 'in_progress') : 'pending';

      const calcCharacteristic = (fieldKey: keyof SessionStateCommunity): ProgressStatus => {
      if (submit !== 'completed') return 'pending';
      const communities = sessionState.communities || [];
      if (communities.length === 0) return 'pending';
      const allAnsweredManually = communities.every((c) => {
        const tf = c[fieldKey] as TrackedField<any> | undefined;
        return tf && tf.source === 'manual' && (tf.value === true || tf.value === false);
      });
      if (allAnsweredManually) return 'completed';
      const anyTouched = communities.some((c) => {
        const tf = c[fieldKey] as TrackedField<any> | undefined;
        return !!tf && (tf.value === true || tf.value === false);
      });
      return anyTouched ? 'in_progress' : 'in_progress';
    };

    const char_income = calcCharacteristic('affordable_income_restricted');
    const char_section8 = submit === 'completed' && char_income === 'completed' ? calcCharacteristic('affordable_section_8') : 'pending';
    const char_newconstruction = submit === 'completed' && char_section8 === 'completed' ? calcCharacteristic('new_construction') : 'pending';
    const char_student = submit === 'completed' && char_newconstruction === 'completed' ? calcCharacteristic('student') : 'pending';

    // Shared Operations progress based on clo_groups presence
    let char_sharedops: ProgressStatus = 'pending';
    if (submit === 'completed' && char_student === 'completed') {
      const groups = sessionState.clo_groups;
      if (groups === null) {
        // No data provided yet – needs attention
        char_sharedops = 'in_progress';
      } else if (Array.isArray(groups) && groups.length === 0) {
        // Explicitly set to empty – user said no groups; treat as completed
        char_sharedops = 'completed';
      } else {
        const allHaveCommunities = (groups || []).every(g => Array.isArray(g.communities) && g.communities.length > 0);
        char_sharedops = allHaveCommunities ? 'completed' : 'in_progress';
      }
    }

    // Submit for Review progress
    let char_submit: ProgressStatus = 'pending';
    if (char_sharedops !== 'pending') {
      char_submit = sessionState.submitted_characteristics ? 'completed' : 'in_progress';
    }

    // Implementation Contacts progress: unlocked after characteristics submit
    const isContactsUnlocked = char_submit !== 'pending';
    const communities = sessionState.communities || [];
    const checkPerson = (key: keyof SessionStateCommunity): ProgressStatus => {
      if (!isContactsUnlocked) return 'pending';
      if (communities.length === 0) return 'pending';
      const allFilled = communities.every((c) => {
        const tf = c[key] as TrackedField<SessionStatePerson[]> | undefined;
        return tf && Array.isArray(tf.value) && tf.value.length > 0 && tf.value.every(p => p.email && p.email.trim());
      });
      if (allFilled) return 'completed';
      const anyTouched = communities.some((c) => {
        const tf = c[key] as TrackedField<SessionStatePerson[]> | undefined;
        return tf && Array.isArray(tf.value) && tf.value.length > 0;
      });
      return anyTouched ? 'in_progress' : 'in_progress';
    };

    const impl_project_lead = checkPerson('project_lead_team_member');
    const impl_technical = impl_project_lead !== 'pending' ? checkPerson('technical_team_member') : 'pending';
    const impl_voice = impl_technical !== 'pending' ? checkPerson('voice_team_member') : 'pending';
    const impl_marketing = impl_voice !== 'pending' ? checkPerson('marketing_team_member') : 'pending';
    const impl_knowledge = impl_marketing !== 'pending' ? checkPerson('knowledge_team_member') : 'pending';
    const impl_training = impl_knowledge !== 'pending' ? checkPerson('training_team_member') : 'pending';
    const impl_owner = impl_training !== 'pending' ? checkPerson('owner_team_member') : 'pending';
    const impl_submit: ProgressStatus = impl_owner !== 'pending' ? (sessionState.submitted_pocs ? 'completed' : 'in_progress') : 'pending';

    // PLG progress (only if enabled)
    const isPLG = !!sessionState.product_led_growth;
    const allCommunities = sessionState.communities || [];

    const calcStringField = (key: keyof SessionStateCommunity): ProgressStatus => {
      if (!isPLG) return 'pending';
      if (submit !== 'completed') return 'pending';
      if (allCommunities.length === 0) return 'in_progress';
      const allFilled = allCommunities.every((c) => {
        const tf = c[key] as TrackedField<string> | undefined;
        return !!tf && typeof tf.value === 'string' && tf.value.trim().length > 0;
      });
      return allFilled ? 'completed' : 'in_progress';
    };

    const calcBooleanField = (key: keyof SessionStateCommunity): ProgressStatus => {
      if (!isPLG) return 'pending';
      if (submit !== 'completed') return 'pending';
      if (allCommunities.length === 0) return 'in_progress';
      const allSet = allCommunities.every((c) => {
        const tf = c[key] as TrackedField<boolean> | undefined;
        return !!tf && typeof tf.value === 'boolean';
      });
      return allSet ? 'completed' : 'in_progress';
    };

    const calcVoiceArea = (): ProgressStatus => {
      if (!isPLG) return 'pending';
      if (submit !== 'completed') return 'pending';
      if (allCommunities.length === 0) return 'in_progress';
      const allValid = allCommunities.every((c) => {
        const tf = c['voice_area_code'] as TrackedField<any> | undefined;
        const v = tf?.value;
        if (v === 'general') return true;
        if (typeof v === 'number') return String(v).length === 3;
        // Also handle string values that are 3-digit numbers
        if (typeof v === 'string' && /^\d{3}$/.test(v)) return true;
        return false;
      });
      return allValid ? 'completed' : 'in_progress';
    };

    const plg_name: ProgressStatus = calcStringField('elisego_name');
    const plg_ai_name: ProgressStatus = calcStringField('ai_name');
    const plg_voice_area: ProgressStatus = calcVoiceArea();
    const plg_schedule: ProgressStatus = calcBooleanField('schedule_tours');
    const plg_submit: ProgressStatus = isPLG && submit === 'completed' && plg_schedule !== 'pending' ? 
      (sessionState.submitted_plg ? 'completed' : 'in_progress') : 'pending';

    return { welcome, search, confirm, submit, char_income, char_section8, char_newconstruction, char_student, char_sharedops, char_submit, impl_project_lead, impl_technical, impl_voice, impl_marketing, impl_knowledge, impl_training, impl_owner, impl_submit, plg_name, plg_ai_name, plg_voice_area, plg_schedule, plg_submit };
  }, [sessionState, schema]);
};

function calculateConfirmProgress(
  communities: SessionStateCommunity[], 
  schema: IntakePortalSchema
): ProgressStatus {
  if (communities.length === 0) return 'pending';
  
  const EXCLUDED_KEYS = new Set([
    'affordable_income_restricted',
    'affordable_section_8',
    'new_construction',
    'student',
    // Exclude PLG fields from confirm progress
    'ai_name',
    'elisego_name',
    'schedule_tours',
    'voice_area_code',
  ]);
  
  const allValid = communities.every(community => {
    return schema.intake_fields?.every(field => {
      if (EXCLUDED_KEYS.has(field.key)) return true; // ignore these in confirm progress
      if (!field.visible) return true; // skip hidden fields
      if (!field.required) return true;
      
      const fieldValue = community[field.key as keyof SessionStateCommunity];
      if (!fieldValue || typeof fieldValue !== 'object' || !('value' in fieldValue)) return false;
      
      const trackedField = fieldValue as TrackedField<any>;
      if (!trackedField.value) return false;
      
      // Validate person fields
      if (isPersonField(getFieldType(field.key))) {
        const persons = trackedField.value as SessionStatePerson[];
        // Ensure persons is an array
        if (!Array.isArray(persons) || persons.length === 0) return false;
        
        const requiredFields = field.required_person_fields?.[0];
        if (!requiredFields) return true;
        
        return persons.every(person => {
          if (requiredFields.name && !person.name?.trim()) return false;
          if (requiredFields.title && !person.title?.trim()) return false;
          if (requiredFields.email && !person.email?.trim()) return false;
          return true;
        });
      }
      
      // Validate regular fields
      if (Array.isArray(trackedField.value)) {
        return trackedField.value.length > 0;
      }
      
      return trackedField.value !== null && 
             trackedField.value !== undefined && 
             String(trackedField.value).trim() !== '';
    });
  });
  
  return allValid ? 'completed' : 'in_progress';
}